import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, FormControl, FormHelperText, Grid, InputLabel, Select, TextField } from '@material-ui/core';
import { fetchInspectedVehicles, fetchInspectedVehiclesByDocument } from 'app/store/slices/vehicle';
import { FormServiceContext } from 'app/utils/contexts';
import lineEndpoint from 'app/api/services/lineEndpoint';
import vehiclesEndpoint from 'app/api/services/vehiclesEndpoint';
import moment from 'moment';
import notification from 'app/config/notification';
import { URLS } from 'app/config/urls';
import { generatePath, useHistory } from 'react-router-dom';

const ChangeVehicle = (props) => {
    const { classes, permissionaryData, handleChange, setFieldValue, values, errors, touched } = props;
    const dispatch = useDispatch();
    const history = useHistory();
    const { canEdit, isInternalForm } = useContext(FormServiceContext);

    const [loading, setLoading] = useState(true);
    const [veiculoSelecionado, setVeliculoSelecionado] = useState({});
    const [inspectedVehiclesFiltered, setInspectedVehiclesFiltered] = useState([]);
    const [activeVehicle, setActiveVehicle] = useState({});

    const inspectedVehicles = useSelector((state) => state.vehicle.inspectedVehicles);
    const internalNewRequest = useSelector((state) => state.forms.internalNewRequest);
    const request = useSelector((state) => state.requests.details);
    const requestJsonData = useMemo(() => {
        return request.jsonSolicitacao ? JSON.parse(request.jsonSolicitacao) : {};
    }, [request]);

    const handleChangeVehicle = useCallback(
        (vehicleId) => {
            if (!!vehicleId) {
                const foundVehicle = inspectedVehiclesFiltered.find(
                    (veiculo) => veiculo.cdVeiculo === Number(vehicleId),
                );
                setVeliculoSelecionado(foundVehicle);
                if (foundVehicle?.nuAnoFabricacao && activeVehicle?.nuAnoFabricacao) {
                    const nuAnoActiveVehicle = Number(activeVehicle.nuAnoFabricacao);
                    const nuAnoFoundVehicle = Number(foundVehicle.nuAnoFabricacao);
                    if (nuAnoActiveVehicle > nuAnoFoundVehicle) {
                        notification(
                            'Para realizar a substituição do veículo é necessário que o veículo novo tenha o ano de fabricação igual ou superior ao atual',
                            { toastId: 'ERROS_ACTIVE_VEHICLE', type: 'error' },
                        );
                        setFieldValue('cdVeiculo', '');
                        setVeliculoSelecionado({});
                    }
                }
            } else {
                setVeliculoSelecionado({});
            }
        },
        [activeVehicle, inspectedVehiclesFiltered, setFieldValue],
    );

    useEffect(() => {
        (() => {
            if (values.cdVeiculo) {
                handleChangeVehicle(values.cdVeiculo);
            }
        })();
    }, [handleChangeVehicle, values]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            let document = requestJsonData.deCpfCnpj || request.cdUsuarioSolicitacao;
            if (internalNewRequest && internalNewRequest.company) {
                document =
                    internalNewRequest.company.deCnpjMatriz ||
                    internalNewRequest.company.deCpfCnpj ||
                    internalNewRequest.cdUsuario;
                await dispatch(fetchInspectedVehiclesByDocument(document));
            } else if (isInternalForm) {
                await dispatch(fetchInspectedVehiclesByDocument(document));
            } else {
                if (inspectedVehicles.length === 0) {
                    await dispatch(fetchInspectedVehicles());
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        (async () => {
            let activeVehicleTemp = { ...activeVehicle };
            if (Object.keys(activeVehicleTemp).length === 0) {
                if (requestJsonData.veiculoAntigo && Object.keys(requestJsonData.veiculoAntigo).length !== 0) {
                    activeVehicleTemp = { ...requestJsonData.veiculoAntigo };
                    setFieldValue('cdVeiculoAntigo', activeVehicleTemp.cdVeiculo);
                } else {
                    if (permissionaryData) {
                        const response = await lineEndpoint.getActiveVehicleByPermissionary(
                            permissionaryData.cdLinhaPermissionario,
                        );
                        if (response && response.status === 200 && response.data) {
                            const temp = { ...response.data };
                            activeVehicleTemp = { ...temp.veiculo };
                            setFieldValue('cdVeiculoAntigo', temp.cdVeiculo);
                        }
                    }
                }
            }

            if (Object.keys(activeVehicleTemp).length !== 0) {
                const certificateResponse = await vehiclesEndpoint.getVehicleCertificate(activeVehicleTemp.cdVeiculo);
                if (certificateResponse && certificateResponse.status === 200) {
                    activeVehicleTemp.certificado = certificateResponse.data;
                }
                setActiveVehicle(activeVehicleTemp);
            } else {
                if (!isInternalForm && permissionaryData) {
                    notification('Nenhum veículo ativo encontrado', { toastId: 'ERROS_ACTIVE_VEHICLE', type: 'error' });
                    history.push(generatePath(URLS.SERVICES));
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissionaryData]);

    useEffect(() => {
        (async () => {
            if (inspectedVehicles.length > 0) {
                setLoading(true);
                let tempInspectedVehicles = [];
                for (const iv of inspectedVehicles) {
                    if (iv.cdVeiculo !== activeVehicle.cdVeiculo) {
                        const temp = { ...iv };
                        const certificateResponse = await vehiclesEndpoint.getVehicleCertificate(temp.cdVeiculo);
                        if (certificateResponse && certificateResponse.status === 200) {
                            temp.certificado = certificateResponse.data;
                        }
                        tempInspectedVehicles.push(temp);
                    }
                }
                if (isInternalForm && values.cdVeiculo) {
                    const isCdVeiculoAntigoInTempInspectedVehicles = tempInspectedVehicles.find(
                        (v) => v.cdVeiculo === values.cdVeiculo,
                    );
                    if (!isCdVeiculoAntigoInTempInspectedVehicles) {
                        const response = await vehiclesEndpoint.getVehicle(values.cdVeiculo);
                        if (response?.data) {
                            let newVehicle = response.data;
                            const certificateResponse = await vehiclesEndpoint.getVehicleCertificate(
                                newVehicle.cdVeiculo,
                            );
                            newVehicle.certificado =
                                certificateResponse?.status === 200 ? certificateResponse.data : {};
                            tempInspectedVehicles.push(newVehicle);
                        }
                    }
                }
                setInspectedVehiclesFiltered(tempInspectedVehicles);
                setLoading(false);
            }
        })();
    }, [inspectedVehicles, activeVehicle, isInternalForm, values.cdVeiculo]);

    if (loading) {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <div className={classes.centerProgress}>
                        <CircularProgress size={20} />
                    </div>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid item container spacing={3}>
            <Grid item xs={12}>
                <h3 className={classes.yellowTitle}>Veículo anterior</h3>
            </Grid>
            <Grid item container xs={12} spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormControl variant="filled" margin="dense" disabled fullWidth>
                        <InputLabel shrink={true} htmlFor={'veiculoSubstituto'}>
                            Placa
                        </InputLabel>
                        <Field name="cdVeiculoAntigo" native as={Select} value={activeVehicle?.cdVeiculo}>
                            <option aria-label="None" value="" />
                            <option value={activeVehicle?.cdVeiculo}>{activeVehicle?.dePlaca}</option>
                        </Field>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name=""
                        label="RENAVAM"
                        margin="dense"
                        variant="filled"
                        fullWidth
                        disabled
                        value={activeVehicle?.deRenavam}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name=""
                        label="Data Vencimento Vistoria"
                        margin="dense"
                        variant="filled"
                        fullWidth
                        disabled
                        value={
                            activeVehicle?.certificado?.dtVencimentoCrc
                                ? moment.utc(activeVehicle?.certificado?.dtVencimentoCrc).format('DD/MM/YYYY')
                                : ''
                        }
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name=""
                        label="CNPJ / CPF proprietário"
                        margin="dense"
                        variant="filled"
                        fullWidth
                        disabled
                        value={activeVehicle?.deCpfCnpjProprietario}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name=""
                        label="Nome do Proprietário"
                        margin="dense"
                        variant="filled"
                        fullWidth
                        disabled
                        value={activeVehicle?.nmProprietario}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        name=""
                        label="Ano de Fabricação"
                        margin="dense"
                        variant="filled"
                        fullWidth
                        disabled
                        value={activeVehicle?.nuAnoFabricacao}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <h3 className={classes.greenTitle}>Selecione o veículo novo</h3>
            </Grid>
            <Grid item container xs={12} spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormControl
                        variant="filled"
                        margin="dense"
                        disabled={!canEdit}
                        fullWidth
                        error={!!errors.cdVeiculo && touched.cdVeiculo}>
                        <InputLabel shrink={true} htmlFor={'veiculoSubstituto'}>
                            Placa
                        </InputLabel>
                        <Field
                            name="cdVeiculo"
                            native
                            as={Select}
                            validate={(value) => {
                                let error;
                                if (!value) {
                                    error = 'Campo obrigatório';
                                }
                                return error;
                            }}
                            onChange={(e) => {
                                handleChange(e);
                                handleChangeVehicle(e.target.value);
                            }}>
                            <option aria-label="None" value="" />
                            {inspectedVehiclesFiltered.map((v, index) => {
                                return (
                                    <option key={`cdVeiculo-${index}`} value={v.cdVeiculo}>
                                        {v.dePlaca}
                                    </option>
                                );
                            })}
                        </Field>
                        {errors.cdVeiculo && touched.cdVeiculo ? (
                            <FormHelperText>{errors.cdVeiculo}</FormHelperText>
                        ) : null}
                    </FormControl>
                </Grid>
                {veiculoSelecionado && Object.keys(veiculoSelecionado).length > 0 && (
                    <>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                name=""
                                label="RENAVAM"
                                margin="dense"
                                variant="filled"
                                fullWidth
                                disabled
                                value={veiculoSelecionado.deRenavam}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                name=""
                                label="Data Vencimento Vistoria"
                                margin="dense"
                                variant="filled"
                                fullWidth
                                disabled
                                value={
                                    veiculoSelecionado?.certificado?.dtVencimentoCrc
                                        ? moment
                                              .utc(veiculoSelecionado?.certificado?.dtVencimentoCrc)
                                              .format('DD/MM/YYYY')
                                        : ''
                                }
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                name=""
                                label="CNPJ / CPF"
                                margin="dense"
                                variant="filled"
                                fullWidth
                                disabled
                                value={veiculoSelecionado.deCpfCnpjProprietario}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                name=""
                                label="Nome do Proprietário"
                                margin="dense"
                                variant="filled"
                                fullWidth
                                disabled
                                value={veiculoSelecionado.nmProprietario}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                name=""
                                label="Ano de Fabricação"
                                margin="dense"
                                variant="filled"
                                fullWidth
                                disabled
                                value={veiculoSelecionado.nuAnoFabricacao}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Grid>
    );
};

export default ChangeVehicle;
